import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const fmtCurrency = (v) => {
  if (!v) return '0';
  if (v >= 10000000) return `${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return Number(v).toLocaleString('en-IN');
};

// Cost summary for Division Overview + CEO Dashboard
export function CostSummaryWidget({ showTrend = true }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/cost/cost-kpi').then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data || (data.total_consumption === 0 && data.total_revenue === 0)) {
    // Show from trend if current month is empty but we have historical data
    const lastTrend = (data?.trend || []).filter(t => t.cost_pct > 0);
    if (!lastTrend.length) return null;
    // Use latest month with data
    const latest = lastTrend[lastTrend.length - 1];
    if (!data) return null;
    // Override with latest data
    data.net_fnb_pct = latest.cost_pct;
    data.total_consumption = latest.consumption;
    data.total_revenue = latest.revenue;
    data.period_label = latest.month;
  }

  const trendData = (data.trend || []).filter(t => t.cost_pct > 0).map(t => ({
    name: t.month.slice(5) + '/' + t.month.slice(2, 4),
    value: t.cost_pct,
    fullLabel: t.month,
  }));

  const statusColor = data.net_fnb_pct < 30 ? '#2D8B5E' : data.net_fnb_pct < 35 ? '#BA7517' : '#D4726A';
  const statusLabel = data.net_fnb_pct < 30 ? 'Healthy' : data.net_fnb_pct < 35 ? 'Watch' : 'At Risk';

  return (
    <div className="bg-white rounded-[10px] border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="cost-summary-widget">
      {/* Net F&B Cost — big number */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Net F&B Cost</p>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold" style={{ color: '#1B2A4A' }}>{data.net_fnb_pct}%</span>
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
              style={{ backgroundColor: statusColor + '15', color: statusColor }}>
              {statusLabel}
            </span>
          </div>
          <p className="text-[10px]" style={{ color: '#6B7280' }}>
            {fmtCurrency(data.total_consumption)} cost / {fmtCurrency(data.total_revenue)} revenue
          </p>
        </div>
      </div>

      {/* Kitchen cards */}
      {data.kitchen_cards?.length > 0 && (
        <div className="grid grid-cols-4 gap-1.5 mb-3">
          {data.kitchen_cards.slice(0, 8).map(k => (
            <div key={k.kitchen} className="rounded px-2 py-1.5 text-center"
              style={{ backgroundColor: k.status === 'red' ? '#FEF2F2' : k.status === 'amber' ? '#FEF3C7' : '#F0FDF4' }}>
              <p className="text-[9px] truncate" style={{ color: '#6B7280' }}>{k.kitchen}</p>
              <p className="text-xs font-bold"
                style={{ color: k.status === 'red' ? '#D4726A' : k.status === 'amber' ? '#BA7517' : '#2D8B5E' }}>
                {k.cost_pct}%
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Mini trend chart */}
      {showTrend && trendData.length > 1 && (
        <div style={{ height: 80 }}>
          <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 1, height: 1 }}>
            <AreaChart data={trendData} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
              <defs>
                <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={statusColor} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={statusColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: '#9CA3AF' }} axisLine={false} tickLine={false} domain={[0, 50]}
                tickFormatter={v => `${v}%`} width={30} />
              <Tooltip formatter={v => `${v}%`} labelFormatter={l => l} />
              <Area type="monotone" dataKey="value" stroke={statusColor} strokeWidth={2}
                fill="url(#costGrad)" dot={{ r: 3, fill: statusColor, stroke: '#fff', strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// CEO-level cost badge for division card
export function CostBadge() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/cost/cost-kpi').then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  let pct = data.net_fnb_pct;
  if (pct === 0) {
    const lastTrend = (data.trend || []).filter(t => t.cost_pct > 0);
    if (lastTrend.length) pct = lastTrend[lastTrend.length - 1].cost_pct;
    else return null;
  }

  const color = pct < 30 ? '#2D8B5E' : pct < 35 ? '#BA7517' : '#D4726A';
  const label = pct < 30 ? 'healthy' : pct < 35 ? 'watch' : 'at risk';

  return (
    <span className="text-[10px] font-semibold" style={{ color }} data-testid="cost-badge">
      F&B: {pct}% ({label})
    </span>
  );
}
